import { Button, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface DocumentsPreviewPanelProps {
    fileType: string | null;
    preview: string | null;
    pdfData: string | null;
    numPages: number;
    scale: number;
    loading: boolean;
    onScaleChange: (scale: number) => void;
    onNumPagesChange: (numPages: number) => void;
    onUnsupported: () => void;
}

export default function DocumentsPreviewPanel({
    fileType, preview, pdfData, numPages, scale, loading,
    onScaleChange, onNumPagesChange, onUnsupported
}: DocumentsPreviewPanelProps) {
    const { t } = useTranslation();

    return (
        <div className="documents-preview-panel flex-grow-1">
            {fileType === "image" && preview && (
                <div className="documents-preview-image-wrapper">
                    <img src={preview} alt="preview" className="documents-preview-image" />
                </div>
            )}

            {fileType === "text" && preview && (
                <pre className="documents-preview-text">{preview}</pre>
            )}

            {fileType === "pdf" && pdfData && (
                <div className="documents-preview-pdf">
                    <div className="documents-preview-toolbar">
                        <Button
                            variant="link"
                            size="sm"
                            onClick={() => onScaleChange(Math.max(0.5, scale - 0.1))}
                            className="documents-preview-toolbar-button"
                        >
                            <i className="bi bi-dash-lg" />
                        </Button>
                        <span className="documents-preview-toolbar-scale">
                            {Math.round(scale * 100)}%
                        </span>
                        <Button
                            variant="link"
                            size="sm"
                            onClick={() => onScaleChange(Math.min(3, scale + 0.1))}
                            className="documents-preview-toolbar-button"
                        >
                            <i className="bi bi-plus-lg" />
                        </Button>
                        <span className="documents-preview-toolbar-pages">
                            {numPages} {t("common:documents.pages")}
                        </span>
                    </div>

                    <div className="documents-preview-pdf-body">
                        <Document
                            file={pdfData}
                            onLoadSuccess={({ numPages }) => onNumPagesChange(numPages)}
                            onLoadError={(err) => { console.error("react-pdf error:", err); onUnsupported(); }}
                            loading={<div className="documents-preview-loading"><Spinner animation="border" variant="light" /></div>}
                        >
                            {Array.from({ length: numPages }, (_, i) => (
                                <div key={`page_${i + 1}`} className="documents-preview-page">
                                    <Page pageNumber={i + 1} scale={scale} renderTextLayer renderAnnotationLayer />
                                </div>
                            ))}
                        </Document>
                    </div>
                </div>
            )}

            {fileType === "unsupported" && (
                <p className="text-center text-muted mt-5">{t("common:documents.unsupportedFile")}</p>
            )}

            {!fileType && !loading && !preview &&(
                <p className="text-center text-muted mt-5">{t("common:documents.selectDocumentToPreview")}</p>
            )}
        </div>
    );
}
